import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, MessageCircle, Phone, Star,
  Shield, Award, Clock, ChevronDown, Plus,
} from 'lucide-react';
import Tilt3D from '../components/Tilt3D';
import BlurFade from '../components/primitives/BlurFade';
import MUIMarquee from '../components/primitives/Marquee';
import CountUp from '../components/primitives/CountUp';
import HeroBlock from '../components/blocks/HeroBlock';
import SplitBlock from '../components/blocks/SplitBlock';
import MarqueeBlock from '../components/blocks/MarqueeBlock';
import GridBlock from '../components/blocks/GridBlock';
import StepsBlock from '../components/blocks/StepsBlock';
import BenefitsBlock from '../components/blocks/BenefitsBlock';
import QuoteBlock from '../components/blocks/QuoteBlock';
import AboutBlock from '../components/blocks/AboutBlock';
import SamplePromoBlock from '../components/blocks/SamplePromoBlock';
import LeadFormBlock from '../components/blocks/LeadFormBlock';
import FinalCTABlock from '../components/blocks/FinalCTABlock';
import LifestyleBlock from '../components/blocks/LifestyleBlock';
import TypeCompareBlock from '../components/blocks/TypeCompareBlock';
import SampleKitBlock from '../components/blocks/SampleKitBlock';
import BundlesBlock from '../components/blocks/BundlesBlock';
import { useLang } from '../LangContext';
import { company, media } from '../i18n';
import { mediaAlt } from '../lib/mediaSeo';
import { usePrices, categoryFromPrice, getBundles } from '../hooks/usePrices';
import Seo from '../components/Seo';

gsap.registerPlugin(ScrollTrigger);

// Stable framer-motion animation configs. Defined once at module scope so
// referential equality is preserved across renders — prevents children from
// re-rendering when only the parent state changes.
const COLLAPSE_INITIAL = { height: 0, opacity: 0 };
const COLLAPSE_ANIMATE = { height: 'auto', opacity: 1 };
const COLLAPSE_EXIT    = { height: 0, opacity: 0 };
const EASE_OUT_EXPO    = [0.22, 1, 0.36, 1];
const COLLAPSE_T_45    = { duration: 0.45, ease: EASE_OUT_EXPO };
const COLLAPSE_T_35    = { duration: 0.35, ease: EASE_OUT_EXPO };
const STICKY_INITIAL   = { y: 80, opacity: 0 };
const STICKY_ANIMATE   = { y: 0,  opacity: 1 };
const STICKY_EXIT      = { y: 80, opacity: 0 };
const STICKY_T         = { duration: 0.35, ease: EASE_OUT_EXPO };

const telHref = (raw) => 'tel:' + raw.replace(/[^+\d]/g, '').replace(/^(\+49)0/, '$1');
const waHref = (text) =>
  `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(text || 'Hallo Horrenmaker, ik heb een vraag.')}`;

// ----- Eyebrow + Title primitives
const Eyebrow = ({ children, className = '', light = false }) => (
  <p className={`reveal-up text-[11.5px] uppercase tracking-[0.22em] font-medium ${light ? 'text-white/55' : 'text-[#111]/55'} ${className}`}>
    {children}
  </p>
);
const Title = ({ children, className = '', light = false, after = null }) => (
  <h2 className={`font-display ${light ? 'text-white' : 'text-[#111]'} ${className}`}>
    <span className="reveal-line"><span>{children}</span></span>
    {after && (
      <span className="reveal-line block"><span style={{ transitionDelay: '0.15s' }}>{after}</span></span>
    )}
  </h2>
);

// ----- EMPATHY (pain spiegel)
export function Empathy({ t, data = null }) {
  const { lang, overlay } = useLang();
  const imgRef = useRef(null);
  const sectionRef = useRef(null);
  const model = data || t.empathy;
  const title = model.title || model.headline || '';
  const titleAfter = model.titleAfter || model.headlineAccent || '';
  const img = model.mediaSrc || media.empathy;
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(imgRef.current, {
        yPercent: -8, ease: 'none',
        scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', end: 'bottom top', scrub: 1 },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return (
    <section ref={sectionRef} id="empathy" data-testid="empathy-section"
      className="relative bg-[#FAF8F2] text-[#1F1D1A] overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-6 relative aspect-[4/3] lg:aspect-auto lg:min-h-[100vh] overflow-hidden">
          <img ref={imgRef} src={img} alt={mediaAlt(overlay, img, lang, title || model.eyebrow || '')} className="absolute inset-0 w-full h-[115%] object-cover will-change-transform" />
        </div>
        <div className="lg:col-span-6 px-5 lg:px-16 py-20 lg:py-32 flex flex-col justify-center">
          <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
          <h2 className="font-display text-[36px] sm:text-[52px] lg:text-[72px] text-[#1F1D1A]">
            <span className="reveal-line block"><span>{title}</span></span>
            <span className="reveal-line block"><span style={{ transitionDelay: '0.15s' }} className="text-[#F26B1A]">{titleAfter}</span></span>
          </h2>
          <p className="reveal-up delay-2 mt-7 text-[#5C5852] text-[15.5px] leading-relaxed max-w-md">
            {model.body}
          </p>
        </div>
      </div>
    </section>
  );
}

// ----- PROMISE
export function Promise({ t, data = null }) {
  const { lang, overlay } = useLang();
  const imgRef = useRef(null);
  const sectionRef = useRef(null);
  const model = data || t.promise;
  const img = model.mediaSrc || media.promise;
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(imgRef.current, {
        yPercent: -10, ease: 'none',
        scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', end: 'bottom top', scrub: 1 },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return (
    <section ref={sectionRef} data-testid="promise-section" className="relative bg-[#FAF8F4] py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-20 items-end">
          <Title className="lg:col-span-7 text-[40px] sm:text-[62px] lg:text-[88px] xl:text-[96px] leading-[1.06] max-w-[12ch] sm:max-w-none">{model.title}</Title>
          <p className="reveal-up delay-2 lg:col-span-4 lg:col-start-9 text-[#111]/60 text-[15.5px] leading-relaxed max-w-md">{model.body}</p>
        </div>
        <div className="mt-16 lg:mt-24 relative overflow-hidden rounded-[22px] aspect-[16/9] lg:aspect-[21/9] depth-card">
          <img ref={imgRef} src={img} alt={mediaAlt(overlay, img, lang, model.title || model.eyebrow || '')} className="absolute inset-0 w-full h-[118%] object-cover will-change-transform" />
        </div>
      </div>
    </section>
  );
}

// ----- PRODUCTS as SCENES
// ----- COMPARISON slider
export function Comparison({ t, data = null }) {
  const { lang, overlay } = useLang();
  const [pos, setPos] = useState(50);
  const wrapRef = useRef(null);
  const dragging = useRef(false);
  const model = data || t.comparison;
  const beforeImage = model.imageBefore || media.comparisonA;
  const afterImage = model.imageAfter || media.comparisonB;
  const hasProductLinks = !!(model.linkBefore || model.linkAfter);
  const setFromClientX = (x) => {
    const r = wrapRef.current.getBoundingClientRect();
    setPos(Math.max(2, Math.min(98, ((x - r.left) / r.width) * 100)));
  };
  const onMove = (e) => { if (!dragging.current) return; setFromClientX(e.touches ? e.touches[0].clientX : e.clientX); };
  useEffect(() => {
    const up = () => (dragging.current = false);
    window.addEventListener('mouseup', up); window.addEventListener('touchend', up);
    window.addEventListener('mousemove', onMove); window.addEventListener('touchmove', onMove, { passive: true });
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', onMove); window.removeEventListener('touchmove', onMove); };
    // Mount-once listener registration; `onMove` reads always-fresh refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (hasProductLinks) {
    const cards = [
      { image: beforeImage, label: model.labelBefore, to: model.linkBefore, caption: model.captionBefore },
      { image: afterImage, label: model.labelAfter, to: model.linkAfter, caption: model.captionAfter },
    ].filter((item) => item.image || item.label);
    return (
      <section id="voor-na" data-testid="comparison-section" className="relative bg-[#FAF8F4] py-24 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
          <Title className="text-[36px] sm:text-[52px] lg:text-[76px] max-w-3xl mb-6">{model.title}</Title>
          {model.sub && (
            <p className="reveal-up delay-1 text-[#5C5852] text-[15.5px] lg:text-[17px] leading-relaxed max-w-2xl mb-10 lg:mb-14">{model.sub}</p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7">
            {cards.map((card, index) => {
              const Tag = card.to ? Link : 'div';
              const props = card.to ? { to: card.to } : {};
              return (
                <Tag
                  key={`${card.label}-${index}`}
                  {...props}
                  className="group relative block overflow-hidden rounded-[22px] aspect-[4/3] bg-white border border-black/[0.06] depth-card"
                >
                  {card.image && (
                    <img
                      src={card.image}
                      alt={mediaAlt(overlay, card.image, lang, card.label || model.title || '')}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                  <div className="absolute left-5 right-5 bottom-5 text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.35)]">
                    {card.to && (
                      <span className={`mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.18em] shadow-[0_4px_14px_-4px_rgba(31,29,26,0.35)] ${index === 0 ? 'bg-[#111]/62 backdrop-blur' : 'bg-[#F26B1A]'}`}>
                        Mehr erfahren <ArrowRight className="w-3 h-3" />
                      </span>
                    )}
                    {card.label && (
                      <h3 className="font-display text-[32px] lg:text-[44px] leading-[1.02] max-w-[11ch]">
                        {card.label}
                      </h3>
                    )}
                    {card.caption && (
                      <p className="mt-3 max-w-sm text-[14px] lg:text-[15.5px] leading-relaxed text-white/88">
                        {card.caption}
                      </p>
                    )}
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section id="voor-na" data-testid="comparison-section" className="relative bg-[#FAF8F4] py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
        <Title className="text-[36px] sm:text-[52px] lg:text-[76px] max-w-3xl mb-6">{model.title}</Title>
        {model.sub && (
          <p className="reveal-up delay-1 text-[#5C5852] text-[15.5px] lg:text-[17px] leading-relaxed max-w-2xl mb-10 lg:mb-14">{model.sub}</p>
        )}
        <div ref={wrapRef} data-testid="comparison-slider"
          className="relative w-full aspect-[16/10] lg:aspect-[21/10] rounded-[22px] overflow-hidden depth-card select-none cursor-ew-resize"
          onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
          onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}>
          <img src={afterImage} alt={mediaAlt(overlay, afterImage, lang, model.labelAfter || model.title || '')} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
          <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
            <img src={beforeImage} alt={mediaAlt(overlay, beforeImage, lang, model.labelBefore || model.title || '')} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
          </div>
          <span className="absolute top-5 left-5 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.18em] bg-[#111]/55 backdrop-blur text-white">{model.labelBefore}</span>
          <span className="absolute top-5 right-5 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.18em] bg-[#F26B1A] text-white shadow-[0_4px_14px_-4px_rgba(242,107,26,0.6)]">{model.labelAfter}</span>
          <div className="absolute top-0 bottom-0 w-px bg-white/90" style={{ left: `${pos}%` }}>
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white text-[#1F1D1A] shadow-2xl inline-flex items-center justify-center">
              <ArrowRight className="w-3.5 h-3.5 -ml-1.5" /><ArrowRight className="w-3.5 h-3.5 -mr-1.5 rotate-180" />
            </div>
          </div>
          {/* Mobile hint pill */}
          {model.hint && (
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-[11px] bg-white/90 backdrop-blur text-[#1F1D1A] sm:hidden">
              ← {model.hint} →
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ----- REVIEWS / TESTIMONIAL MARQUEE
export function Reviews({ t, data = null }) {
  const model = data || t.reviews;
  const items = [...(model.items || []), ...(model.items || [])];
  return (
    <section data-testid="reviews-section" className="relative bg-white py-24 lg:py-36 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 mb-14">
        <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
        <Title className="text-[36px] sm:text-[52px] lg:text-[72px] max-w-3xl">{model.title}</Title>
      </div>
      <div className="relative">
        <div className="marquee-track gap-5">
          {items.map((r, i) => (
            <article key={`review-${r.name}-${r.city}-${i}`} data-testid={`review-card-${i}`}
              className="shrink-0 w-[320px] sm:w-[400px] bg-[#FAF8F4] border border-black/[0.06] rounded-[20px] p-6 lg:p-8 mx-2">
              <div className="flex gap-0.5 mb-4">
                {[0,1,2,3,4].map(s => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="font-display text-[#111]/90 text-[18px] lg:text-[20px] leading-snug">"{r.text}"</p>
              <p className="mt-6 text-[13px] text-[#111]/65"><span className="text-[#111] font-medium">{r.name}</span> · {r.city}</p>
            </article>
          ))}
        </div>
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 w-20 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-20 bg-gradient-to-l from-white to-transparent" />
      </div>
    </section>
  );
}

// ----- TRUST DEEP-DIVE (atelier + badges)
export function TrustDeep({ t, data = null }) {
  const { lang, overlay } = useLang();
  const imgRef = useRef(null);
  const sectionRef = useRef(null);
  const model = data || t.trust;
  const img = model.mediaSrc || media.atelier;
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(imgRef.current, { scale: 1.08 }, {
        scale: 1.0, ease: 'none',
        scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', end: 'bottom top', scrub: 1.2 },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  const icons = [Award, Star, Shield, Clock];
  return (
    <section ref={sectionRef} id="garantie" data-testid="trust-deep" className="relative bg-[#FAF8F4] py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-20 items-center">
        <Tilt3D max={5} lift={16} className="lg:col-span-6 rounded-[22px] overflow-hidden aspect-[4/5] lg:aspect-[4/4.5] depth-card">
          <img ref={imgRef} src={img} alt={mediaAlt(overlay, img, lang, model.title || model.eyebrow || '')} className="absolute inset-0 w-full h-full object-cover will-change-transform" />
        </Tilt3D>
        <div className="lg:col-span-6">
          <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
          <h2 className="font-display text-[#111] text-[40px] sm:text-[58px] lg:text-[76px]">
            <span className="reveal-line block"><span>{model.title}</span></span>
            <span className="reveal-line block"><span style={{ transitionDelay: '0.15s' }} className="text-[#111]/55">{model.titleAfter}</span></span>
          </h2>
          <p className="reveal-up delay-2 mt-6 text-[#111]/60 text-[15.5px] leading-relaxed max-w-md">{model.body}</p>
          <div className="reveal-up delay-3 mt-12 grid grid-cols-2 gap-y-8 gap-x-6">
            {(model.badges || []).map((b, i) => {
              const Icon = icons[i];
              // Parse "30+" → numeric 30 with suffix "+"; non-numeric stays as text
              const m = String(b.n).match(/^(\d+)(.*)$/);
              return (
                <div key={`badge-${b.l}`} className="flex items-start gap-4">
                  <Icon className="w-5 h-5 text-[#F26B1A] mt-1.5 shrink-0" />
                  <div>
                    <p className="font-display text-[42px] lg:text-[56px] leading-none text-[#1F1D1A]">
                      {m
                        ? <CountUp to={parseInt(m[1], 10)} suffix={m[2]} duration={1.8} />
                        : b.n}
                    </p>
                    <p className="text-[#5C5852] text-[12.5px] uppercase tracking-[0.15em] mt-2.5">{b.l}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ----- CONFIGURATOR (collapsed accordion)
function ConfiguratorAccordion({ t }) {
  const [open, setOpen] = useState(false);
  const url = process.env.REACT_APP_CONFIGURATOR_URL || '';
  return (
    <section id="configurator" data-testid="configurator-section" className="relative bg-[#FAF8F4] py-16 lg:py-24 border-t border-black/[0.06]">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="configurator-toggle"
          className="w-full flex items-center justify-between gap-6 text-left"
        >
          <div>
            <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#111]/55 font-medium mb-2">{t.config.eyebrow}</p>
            <h2 className="font-display text-[#111] text-[24px] sm:text-[32px] lg:text-[42px]">{t.config.title}</h2>
          </div>
          <span className="shrink-0 w-12 h-12 rounded-full border border-[#111]/20 inline-flex items-center justify-center">
            <Plus className={`w-4 h-4 transition-transform duration-500 ${open ? 'rotate-45' : ''}`} />
          </span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={COLLAPSE_INITIAL} animate={COLLAPSE_ANIMATE} exit={COLLAPSE_EXIT}
              transition={COLLAPSE_T_45}
              className="overflow-hidden"
            >
              <div className="mt-10 rounded-[18px] bg-white border border-black/[0.06] overflow-hidden">
                {url ? (
                  <iframe src={url} title="Configurator" data-testid="configurator-iframe" className="w-full h-[640px] border-0" />
                ) : (
                  <div data-testid="configurator-placeholder" className="w-full h-[420px] flex flex-col items-center justify-center text-center px-6">
                    <p className="font-display text-[#111] text-[24px] lg:text-[32px] max-w-md">{t.config.placeholder}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ----- FAQ 5
export function FAQ({ t, data = null }) {
  const [open, setOpen] = useState(0);
  const model = data || t.faq;
  return (
    <section id="faq" data-testid="faq-section" className="relative bg-[#FAF8F4] py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <Eyebrow className="mb-7">{model.eyebrow}</Eyebrow>
        <Title className="text-[34px] sm:text-[50px] lg:text-[72px] mb-12">{model.title}</Title>
        <div className="flex flex-col">
          {(model.items || []).map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={`faq-${it.q}`} data-testid={`faq-item-${i}`} className="border-t border-[#111]/12 last:border-b">
                <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left">
                  <span className="font-display text-[#111] text-[19px] lg:text-[24px]">{it.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#111] shrink-0 transition-transform duration-400 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={COLLAPSE_INITIAL} animate={COLLAPSE_ANIMATE} exit={COLLAPSE_EXIT}
                      transition={COLLAPSE_T_35} className="overflow-hidden">
                      <p className="pb-7 pr-12 text-[#111]/65 text-[15px] leading-relaxed max-w-2xl">{it.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ----- STICKY MOBILE CTA DOCK
function StickyMobileCTA({ t, lang }) {
  const [visible, setVisible] = useState(false);
  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;
  useEffect(() => {
    let lastY = window.scrollY;
    let raf = null;
    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      // Below 600px scroll → keep hidden (hero area)
      // Above 600px:
      //   scrolling down (delta > 4)  → hide
      //   scrolling up   (delta < -4) → show
      //   near bottom of page         → show (so user reaches the CTA at the end)
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 120;
      if (y < 600) {
        setVisible(false);
      } else if (nearBottom) {
        setVisible(true);
      } else if (delta > 4) {
        setVisible(false);
      } else if (delta < -4) {
        setVisible(true);
      }
      lastY = y;
      raf = null;
    };
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(update);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={STICKY_INITIAL} animate={STICKY_ANIMATE} exit={STICKY_EXIT}
          transition={STICKY_T}
          data-testid="sticky-mobile-cta"
          className="lg:hidden fixed bottom-3 left-3 right-3 z-40 bg-[#111] rounded-full border border-white/10 shadow-[0_18px_40px_rgba(0,0,0,0.45)] flex items-stretch overflow-hidden"
        >
          <a href={telHref(phone)} data-testid="sticky-call"
            className="flex-1 flex items-center justify-center gap-1.5 py-3.5 text-white text-[13px] font-medium border-r border-white/10">
            <Phone className="w-4 h-4" /> {t.sticky.call}
          </a>
          <a href={waHref(lang === 'de' ? 'Hallo, ich möchte ein Angebot.' : 'Hallo, ik wil graag een offerte.')}
            target="_blank" rel="noreferrer" data-testid="sticky-whatsapp"
            className="flex-1 flex items-center justify-center gap-1.5 py-3.5 text-white text-[13px] font-medium border-r border-white/10">
            <MessageCircle className="w-4 h-4" /> {t.sticky.whatsapp}
          </a>
          <a href="#cta-block" data-testid="sticky-offer"
            className="flex-1 flex items-center justify-center gap-1.5 py-3.5 bg-white text-[#111] text-[13px] font-medium">
            {t.sticky.offer} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ----- HomePage — composition of all blocks for the landing page.
export default function HomePage() {
  const { lang, t, c } = useLang();
  const prices = usePrices();

  // Inject live category-min prices into the homepage product cards.
  const products = useMemo(() => {
    const items = c.products.items.map((p) => ({
      ...p,
      price: categoryFromPrice(prices, p.key, lang) || undefined,
    }));
    return { ...c.products, items };
  }, [c.products, prices, lang]);

  const bundles = useMemo(() => getBundles(prices, lang), [prices, lang]);

  return (
    <>
      <Seo
        title={lang === 'de' ? 'Insektenschutz nach Maß' : 'Horren op maat — frisse lucht zonder muggen'}
        description={lang === 'de'
          ? 'Maßgefertigter Insektenschutz für Fenster, Türen und Lichtschächte. 30+ Jahre Erfahrung, 5 Jahre Garantie, niederländischlandweit.'
          : 'Maatwerk horren voor ramen, deuren en lichtschachten. 30+ jaar ervaring, 5 jaar garantie, door heel Nederland.'}
        path="/"
        lang={lang}
      />
      <HeroBlock {...c.hero} bundles={bundles?.items} enableSecretAdminTrigger />
      <BundlesBlock data={bundles} />
      <SplitBlock id="problem" testId="problem-section" {...c.problem} />
      <MarqueeBlock {...c.trustBar} />
      <BenefitsBlock {...c.benefits} tone="sand" />
      <Empathy t={t} />
      <Promise t={t} />
      <GridBlock {...products} />
      <TypeCompareBlock {...c.typeCompare} />
      <LifestyleBlock {...c.lifestyle} />
      <StepsBlock {...c.werkwijze} />
      <Comparison t={t} />
      <SamplePromoBlock {...c.sample} tone="sand" />
      <SampleKitBlock {...c.sampleKit} lang={lang} />
      <QuoteBlock {...c.bigQuote} tone="cream" />
      <Reviews t={t} />
      <AboutBlock {...c.about} tone="cream" />
      <TrustDeep t={t} />
      <LeadFormBlock {...c.leadForm} lang={lang} />
      <ConfiguratorAccordion t={t} />
      <FAQ t={t} />
      <FinalCTABlock {...c.finalCta} />
      <StickyMobileCTA t={t} lang={lang} />
    </>
  );
}
